import { motion } from "framer-motion";
import { PHOTOS } from "@/data/content";

const BIO_1 = [
  "Susan Grau is an internationally recognized grief and trauma expert, globally sought-after keynote speaker, bestselling author, healer, and thought leader whose work has inspired millions of people around the world to transform profound loss into resilience, hope, and purpose.",
  "After experiencing the unimaginable loss of both of her brothers and her mother to suicide, along with several friends, Susan dedicated her life to understanding grief, trauma, suicide loss, and the remarkable resilience of the human spirit. For more than three decades, she has helped individuals, families, organizations, and communities navigate life's most devastating moments, empowering people to move beyond tragedy and rediscover hope after unimaginable loss.",
  "Widely respected for her unique ability to blend lived experience with decades of professional study, Susan has become one of today's most recognized voices on grief, trauma recovery, suicide loss, resilience, personal empowerment, and post-traumatic growth. Her work has impacted millions worldwide and has generated more than 580 million views across television, digital media, podcasts, documentaries, and social platforms.",
];

const BIO_2 = [
  "Susan's journey began after surviving a near-death experience at just four-and-a-half years old, an experience that shaped her lifelong pursuit of healing, resilience, and human transformation. Rather than allowing adversity to define her life, she transformed it into a personal mission dedicated to helping others overcome grief, rebuild their lives, and discover strength they never believed possible.",
  "All Susan ever dreamed of was becoming a mother. After discovering she was infertile, she received the blessing of adopting her children — the role she cherishes above every title she holds. And from 2009 to 2013, Susan lived with a brain tumor, a years-long reckoning that deepened everything she teaches about fear, faith, and the body's capacity to heal.",
  "An internationally acclaimed keynote speaker and thought leader, Susan has delivered transformational lectures at expos and symposiums throughout the United States and Canada. She has been featured on The Drew Barrymore Show, Goop, CBS, The Hollywood Reporter, Barstool Sports, The Art of Being Well with Dr. Will Cole, Oldish with Tori Spelling and Randy Spelling, Valerie Bertinelli's Getting Naked Podcast, and hundreds of leading talk shows, podcasts, documentaries, radio, and national and international media outlets. She also appeared alongside Dr. Eben Alexander in the acclaimed documentary Beyond the Grave, nominated at the Beverly Hills Film Festival.",
  "In March 2026, Susan appeared on The Drew Barrymore Show alongside Valerie Bertinelli — a celebrated conversation on grief, resilience, and hope. That June, she led The Spirit Encounter at the Omega Institute in Rhinebeck, New York, a sold-out event that brought return invitations, with appearances at Esalen Institute ahead. Her debut book, Infinite Life, Infinite Lessons (Hay House), has sold well over 10,000 copies with foreign editions in Turkey and Spain — and nearly 60,000 clients have sat with Susan across her career.",
  "She is widely recognized for changing the conversation around grief, suicide loss, trauma, and healing by helping people understand that while pain may become part of their story, it never has to become their identity. Her mission is to help people transform life's deepest heartbreak into their greatest source of strength, proving that healing is possible, resilience can be learned, and hope can always be found, even after the darkest moments.",
];

const fade = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] },
};

export default function Bio() {
  return (
    <section id="story" data-testid="bio-section" className="relative overflow-hidden py-28 lg:py-40">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 top-10 select-none font-display text-[16rem] font-light italic leading-none text-[#C5A059]/10"
      >
        01
      </span>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:gap-12 lg:px-10">
        <div className="lg:col-span-5">
          <motion.div {...fade}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-[#C5A059]/50" aria-hidden="true" />
              <img
                src={PHOTOS.bio}
                alt="Susan Grau"
                data-testid="bio-portrait"
                className="relative aspect-[4/5] w-full object-cover shadow-[0_20px_40px_-15px_rgba(44,38,32,0.15)]"
              />
              <p className="mt-6 font-script text-3xl text-[#C5A059]">her story</p>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <motion.p {...fade} className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]">
            Chapter 01 — Her Story
          </motion.p>
          <motion.h2
            {...fade}
            transition={{ ...fade.transition, delay: 0.1 }}
            className="mt-6 font-display text-4xl font-light leading-tight tracking-tighter text-[#6B5D4F] sm:text-5xl lg:text-6xl"
          >
            Turning Heartbreak <br />
            <span className="italic text-[#C5A059]">Into Hope</span>
          </motion.h2>

          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.15 }} className="mt-12 space-y-8">
            <p data-testid="bio-lead" className="drop-cap font-body text-base font-light leading-relaxed text-[#6B5D4F] md:text-lg">
              {BIO_1[0]}
            </p>
            {BIO_1.slice(1).map((p, i) => (
              <p key={i} className="font-body text-base font-light leading-relaxed text-[#8B7D6B]">
                {p}
              </p>
            ))}
          </motion.div>

          <motion.blockquote
            {...fade}
            className="my-14 border-l-2 border-[#C5A059] py-2 pl-8"
            data-testid="bio-pullquote"
          >
            <p className="font-display text-2xl font-light italic leading-snug text-[#6B5D4F] md:text-3xl">
              "Pain may become part of your story — it never has to become your identity."
            </p>
          </motion.blockquote>

          <motion.div {...fade} className="space-y-8">
            {BIO_2.map((p, i) => (
              <p key={i} className="font-body text-base font-light leading-relaxed text-[#8B7D6B]">
                {p}
              </p>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        data-testid="credentials-band"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        className="mx-auto mt-24 max-w-7xl border-t border-[#6B5D4F]/10 px-6 pt-16 lg:px-10"
      >
        <p className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]">
          Credentials & Training
        </p>
        <h3 className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-[#6B5D4F] md:text-4xl">
          The Study Behind <span className="italic text-[#C5A059]">the Spirit</span>
        </h3>
        <p className="mt-4 max-w-2xl font-body text-sm font-light leading-relaxed text-[#8B7D6B]">
          A rare blend of academic training, clinical certification, and spiritual scholarship.
        </p>
        <div className="mt-12 border-t border-[#6B5D4F]/10">
          {[
            ["Doctor of Divinity", "Under Dr. Raymond Moody, founder of near-death studies"],
            ["Master Evidential Medium", "Three decades of professional practice"],
            ["CADC II", "Certified addiction counselor & grief specialist"],
            ["Licensed Hypnotherapist", "Specializing in past life regression"],
            ["Reiki Master", "Sai Maa Diksha & Karuna practitioner"],
            ["HTPA", "Healing Touch Professional Association"],
            ["Metaphysical Arts", "Professional teacher of the metaphysical arts"],
            ["Hay House Heal Your Life+", "Reader, speaker & teacher"],
            ["Keynote Speaker", "Hundreds of stages across the U.S. & Canada"],
          ].map(([title, note], i) => (
            <div
              key={title}
              data-testid={`credential-${i}`}
              className="group grid grid-cols-12 items-baseline gap-4 border-b border-[#6B5D4F]/10 py-6"
            >
              <span className="col-span-2 font-display text-lg italic text-[#C5A059] md:col-span-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="col-span-10 font-display text-2xl font-light leading-snug text-[#6B5D4F] transition-transform duration-500 group-hover:translate-x-2 md:col-span-5 md:text-3xl">
                {title}
              </p>
              <p className="col-span-10 col-start-3 font-body text-sm font-light leading-relaxed text-[#8B7D6B] md:col-span-6 md:col-start-7">
                {note}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
